import { useState } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { completeSetup, updateProfile } from "../services/api";

export function useSetupCard() {
	const stripe = useStripe();
	const elements = useElements();
	const [processing, setProcessing] = useState(false);
	const [error, setError] = useState(null);

	const setupCard = async (
		userId,
		setupIntentClientSecret,
		profileData = null
	) => {
		if (!stripe || !elements) {
			throw new Error("Stripe not loaded");
		}

		if (!userId || !setupIntentClientSecret) {
			throw new Error("User ID and setup intent are required");
		}

		setProcessing(true);
		setError(null);

		try {
			const cardElement = elements.getElement(CardElement);

			if (!cardElement) {
				throw new Error(
					"Card element not found. Please refresh and try again."
				);
			}

			// Confirm setup intent
			const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
				setupIntentClientSecret,
				{
					payment_method: {
						card: cardElement,
						billing_details: profileData
							? {
									name: `${profileData.firstName} ${profileData.lastName}`,
									email: profileData.email,
							  }
							: undefined,
					},
				}
			);

			if (stripeError) {
				// Handle specific Stripe error types
				if (stripeError.type === "authentication_required") {
					throw new Error("Additional authentication required. Please try a different payment method or contact your bank.");
				}
				throw new Error(stripeError.message);
			}

			if (setupIntent.status === "requires_action") {
				throw new Error("Additional authentication required. Please try again or use a different payment method.");
			}

			if (setupIntent.status !== "succeeded") {
				throw new Error("Card setup failed. Please check your payment details and try again.");
			}

			// Update profile if provided
			if (profileData) {
				await updateProfile(
					userId,
					profileData.firstName,
					profileData.lastName,
					profileData.email
				);
			}

			// Complete setup on backend
			await completeSetup(userId, setupIntent.id, setupIntent.payment_method);

			return {
				success: true,
				paymentMethodId: setupIntent.payment_method,
				setupIntentId: setupIntent.id,
			};
		} catch (err) {
			setError(err.message);
			throw err;
		} finally {
			setProcessing(false);
		}
	};

	const clearError = () => setError(null);

	return {
		setupCard,
		processing,
		error,
		clearError,
		isStripeLoaded: stripe && elements,
	};
}
